import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Download, LogOut, Users, Building2, RefreshCw, Bell, Plus, X, Mail, KeyRound, UserPlus, ShieldCheck, Trash2, Eye, Check } from "lucide-react";
import { api } from "../lib/site";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";

const TOKEN_KEY = "hrdirect_admin_token";

const roleLabel = (role) => (role === "owner" ? "Website Manager" : "Branch Manager");

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [apps, setApps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [revealed, setRevealed] = useState({});
  const [loading, setLoading] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const h = { headers: { Authorization: `Bearer ${tk}` } };
      const meRes = await api.get("/admin/me", h);
      setMe(meRes.data);
      if (meRes.data.must_change_password) { setLoading(false); return; }
      const a = await api.get("/applications", h);
      setApps(a.data);
      if (meRes.data.role === "owner") {
        try {
          const r = await api.get("/employer-requests", h);
          setReqs(r.data);
        } catch { /* non-fatal */ }
        try {
          const s = await api.get("/admin/settings", h);
          setNotifyEmails(s.data.notify_emails || []);
        } catch { /* non-fatal */ }
      }
    } catch {
      toast.error("Session expired. Please log in again.");
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { username: email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setMe(null); };

  const saveEmails = async (emails) => {
    try {
      const res = await api.put("/admin/settings", { notify_emails: emails }, authHeader);
      setNotifyEmails(res.data.notify_emails || []);
      return true;
    } catch {
      toast.error("Could not save. Please try again.");
      return false;
    }
  };

  const addEmail = async (e) => {
    e.preventDefault();
    const val = newEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error("Enter a valid email address."); return; }
    if (notifyEmails.includes(val)) { toast.error("That email is already added."); return; }
    const ok = await saveEmails([...notifyEmails, val]);
    if (ok) { setNewEmail(""); toast.success("Alert email added."); }
  };

  const removeEmail = async (val) => {
    const ok = await saveEmails(notifyEmails.filter((x) => x !== val));
    if (ok) toast.success("Alert email removed.");
  };

  const revealSsn = async (id) => {
    try {
      const res = await api.get(`/applications/${id}/ssn`, authHeader);
      setRevealed((r) => ({ ...r, [id]: res.data.ssn }));
    } catch {
      toast.error("Could not reveal SSN.");
    }
  };

  const applyContact = (id, fields) => {
    setApps((list) => list.map((a) => (a.id === id ? { ...a, ...fields } : a)));
  };

  const download = async (path, name) => {    try {
      const res = await api.get(`/files/${path}`, { ...authHeader, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      if (name) link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Could not open the resume.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-card border border-border rounded-xl p-8 shadow-sm">
          <img src="/logo-mark.png" alt="HR Direct Services" className="w-14 h-14 object-contain mb-6" />
          <h1 className="font-display uppercase tracking-tight text-2xl">Admin Access</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">HR Direct submissions dashboard</p>
          <Input data-testid="admin-username" type="text" placeholder="Email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} className="mb-3" />
          <Input data-testid="admin-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mb-4" />
          <Button data-testid="admin-login-btn" type="submit" className="w-full bg-cobalt hover:bg-cobalt/90">Sign in</Button>
        </form>
      </div>
    );
  }

  if (me?.must_change_password) {
    return <ForcePasswordChange authHeader={authHeader} onDone={() => load(token)} onCancel={logout} email={me.username} />;
  }

  const isOwner = me?.role === "owner";

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-dashboard">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="HR Direct Services" className="w-8 h-8 object-contain" />
            <span className="font-display uppercase tracking-tight text-lg">HR Direct · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            {me && (
              <span className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="admin-whoami">
                <ShieldCheck size={14} className="text-cobalt" /> {me.username}
                <Badge variant="secondary" className="ml-1">{roleLabel(me.role)}</Badge>
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => load(token)} data-testid="admin-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout"><LogOut size={14} className="mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Users size={16} /> Job Applications</div>
            <div className="font-display text-4xl mt-2 tracking-tight">{apps.length}</div>
          </div>
          {isOwner && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><Building2 size={16} /> Employer Requests</div>
              <div className="font-display text-4xl mt-2 tracking-tight">{reqs.length}</div>
            </div>
          )}
        </div>

        <Tabs defaultValue="applications">
          <TabsList>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
            {isOwner && <TabsTrigger value="employers" data-testid="tab-employers">Employers</TabsTrigger>}
            {isOwner && <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>}
            <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
            {isOwner && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          </TabsList>

          <TabsContent value="applications">
            <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
              <Table data-testid="applications-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Location</TableHead>
                    <TableHead>Office</TableHead><TableHead>SSN</TableHead><TableHead>SMS</TableHead>
                    <TableHead>Resume</TableHead><TableHead>Date</TableHead><TableHead>Contact Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">No applications yet.</TableCell></TableRow>}
                  {apps.map((a) => (
                    <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                      <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                      <TableCell className="text-sm"><div>{a.email}</div><div className="text-muted-foreground">{a.mobile_phone}</div></TableCell>
                      <TableCell className="text-sm">{a.city}, {a.state} {a.zipcode}<div className="text-muted-foreground">{a.country}</div></TableCell>
                      <TableCell className="text-sm">{a.office}</TableCell>
                      <TableCell className="font-mono text-sm" data-testid={`app-ssn-${a.id}`}>
                        <span className="inline-flex items-center gap-2">
                          {revealed[a.id] || a.ssn}
                          {isOwner && !revealed[a.id] && (
                            <button onClick={() => revealSsn(a.id)} data-testid={`reveal-ssn-${a.id}`} className="text-muted-foreground hover:text-cobalt transition-colors" aria-label="Reveal SSN"><Eye size={14} /></button>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{a.text_consent ? <Badge className="bg-green-600">Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                      <TableCell>{a.resume_path ? <Button size="sm" variant="outline" onClick={() => download(a.resume_path, a.resume_filename)} data-testid={`download-${a.id}`}><Download size={13} className="mr-1" /> View</Button> : <span className="text-muted-foreground text-sm">—</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><ContactCell app={a} authHeader={authHeader} onSaved={applyContact} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {isOwner && (
            <TabsContent value="employers">
            <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
              <Table data-testid="employers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Worker Type</TableHead>
                    <TableHead>Duration</TableHead><TableHead>Contact</TableHead><TableHead>Message</TableHead><TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reqs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No employer requests yet.</TableCell></TableRow>}
                  {reqs.map((r) => (
                    <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                      <TableCell className="font-medium">{r.first_name} {r.last_name}</TableCell>
                      <TableCell>{r.company_name}</TableCell>
                      <TableCell className="text-sm">{r.worker_type}</TableCell>
                      <TableCell className="text-sm">{r.duration}</TableCell>
                      <TableCell className="text-sm"><div>{r.email || "—"}</div><div className="text-muted-foreground">{r.phone || ""}</div></TableCell>
                      <TableCell className="text-sm max-w-[220px] truncate">{r.message || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          )}

          {isOwner && (
            <TabsContent value="notifications">
              <div className="bg-card border border-border rounded-xl p-6 max-w-2xl" data-testid="notifications-panel">
                <div className="flex items-center gap-2 mb-1">
                  <Bell size={18} className="text-cobalt" />
                  <h2 className="font-display uppercase tracking-tight text-xl">Alert Emails</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Add the email addresses that should receive an alert every time a job application or employer request is submitted. Add or remove as many as you like — changes take effect immediately.
                </p>

                <form onSubmit={addEmail} className="flex gap-2 mb-6">
                  <Input data-testid="notify-email-input" type="email" placeholder="name@yourcompany.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  <Button type="submit" data-testid="notify-add-btn" className="bg-cobalt hover:bg-cobalt/90 shrink-0"><Plus size={15} className="mr-1" /> Add</Button>
                </form>

                {notifyEmails.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-border rounded-lg">
                    <Mail size={22} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No alert emails yet. Add one above to start receiving submission notifications.</p>
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="notify-email-list">
                    {notifyEmails.map((val) => (
                      <li key={val} className="flex items-center justify-between bg-secondary rounded-lg px-4 py-3" data-testid={`notify-row-${val}`}>
                        <span className="flex items-center gap-2 text-sm"><Mail size={15} className="text-cobalt" /> {val}</span>
                        <button onClick={() => removeEmail(val)} data-testid={`notify-remove-${val}`} className="text-muted-foreground hover:text-destructive transition-colors" aria-label={`Remove ${val}`}>
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="account">
            <ChangePassword authHeader={authHeader} />
          </TabsContent>

          {isOwner && (
            <TabsContent value="users">
              <ManageUsers authHeader={authHeader} meUsername={me?.username} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}

function ContactCell({ app, authHeader, onSaved }) {
  const [contacted, setContacted] = useState(!!app.contacted);
  const [by, setBy] = useState(app.contacted_by || "");
  const [busy, setBusy] = useState(false);

  const save = async (nextContacted, nextBy) => {
    setBusy(true);
    try {
      const res = await api.put(`/applications/${app.id}/contact`, { contacted: nextContacted, contacted_by: nextBy }, authHeader);
      onSaved(app.id, res.data);
      toast.success(nextContacted ? "Marked as contacted." : "Marked as not contacted.");
    } catch {
      toast.error("Could not save. Please try again.");
      setContacted(!!app.contacted);
      setBy(app.contacted_by || "");
    } finally { setBusy(false); }
  };

  const toggle = () => {
    const next = !contacted;
    setContacted(next);
    save(next, next ? by : "");
  };

  return (
    <div className="min-w-[190px]">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`contacted-toggle-${app.id}`}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full overline transition-colors ${contacted ? "bg-green-600 text-white" : "border border-border text-muted-foreground hover:border-cobalt"}`}
      >
        <span className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center ${contacted ? "bg-white/25" : "border border-current"}`}>
          {contacted && <Check size={11} />}
        </span>
        {contacted ? "Contacted" : "Not contacted"}
      </button>

      {contacted && (
        <div className="mt-2">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={by}
              onChange={(e) => setBy(e.target.value)}
              onBlur={() => { if ((by || "") !== (app.contacted_by || "")) save(true, by); }}
              placeholder="Contacted by (name)"
              data-testid={`contacted-by-${app.id}`}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
            />
            <button type="button" onClick={() => save(true, by)} disabled={busy} data-testid={`contacted-save-${app.id}`} className="shrink-0 h-8 px-2 rounded-md bg-cobalt text-white hover:bg-cobalt/90" aria-label="Save contacted by"><Check size={14} /></button>
          </div>
          {app.contacted_branch && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`contacted-meta-${app.id}`}>
              via {app.contacted_branch}{app.contacted_at ? ` · ${new Date(app.contacted_at).toLocaleDateString()}` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ForcePasswordChange({ authHeader, onDone, onCancel, email }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next }, authHeader);
      toast.success("Password set. Welcome!");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center px-6" data-testid="force-change-password">
      <form onSubmit={submit} className="w-full max-w-sm bg-card border border-border rounded-xl p-8 shadow-sm">
        <KeyRound size={28} className="text-cobalt mb-5" />
        <h1 className="font-display uppercase tracking-tight text-2xl">Set your password</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">For security, please choose a new password for <span className="font-medium">{email}</span> before continuing.</p>
        <Input data-testid="fcp-current" type="password" placeholder="Temporary password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mb-3" />
        <Input data-testid="fcp-new" type="password" placeholder="New password (min 8 characters)" value={next} onChange={(e) => setNext(e.target.value)} className="mb-3" />
        <Input data-testid="fcp-confirm" type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mb-4" />
        <Button data-testid="fcp-submit" type="submit" disabled={busy} className="w-full bg-cobalt hover:bg-cobalt/90">{busy ? "Saving…" : "Save & continue"}</Button>
        <button type="button" onClick={onCancel} className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
      </form>
    </div>
  );
}

function ChangePassword({ authHeader }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next }, authHeader);
      toast.success("Password updated.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 max-w-md" data-testid="account-panel">
      <div className="flex items-center gap-2 mb-1"><KeyRound size={18} className="text-cobalt" /><h2 className="font-display uppercase tracking-tight text-xl">Change Password</h2></div>
      <p className="text-sm text-muted-foreground mb-6">Update your own password. You'll need to enter your current password to confirm.</p>
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="cp-current" type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <Input data-testid="cp-new" type="password" placeholder="New password (min 8 characters)" value={next} onChange={(e) => setNext(e.target.value)} />
        <Input data-testid="cp-confirm" type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button data-testid="cp-submit" type="submit" disabled={busy} className="bg-cobalt hover:bg-cobalt/90">{busy ? "Saving…" : "Update password"}</Button>
      </form>
    </div>
  );
}

function ManageUsers({ authHeader, meUsername }) {
  const [users, setUsers] = useState([]);
  const [uName, setUName] = useState("");
  const [uPass, setUPass] = useState("");
  const [uRole, setURole] = useState("branch_manager");
  const [resetFor, setResetFor] = useState("");
  const [resetPass, setResetPass] = useState("");

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/admin/users", authHeader);
      setUsers(res.data);
    } catch { toast.error("Could not load users."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async (e) => {
    e.preventDefault();
    if (uPass.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    try {
      await api.post("/admin/users", { username: uName.trim(), password: uPass, role: uRole }, authHeader);
      toast.success("Account created.");
      setUName(""); setUPass(""); setURole("branch_manager");
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create account."); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (resetPass.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    try {
      await api.post("/admin/users/reset-password", { username: resetFor, new_password: resetPass }, authHeader);
      toast.success(`Password reset for ${resetFor}.`);
      setResetFor(""); setResetPass("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reset password."); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Remove the account "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(name)}`, authHeader);
      toast.success("Account removed.");
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove account."); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="users-panel">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4"><Users size={18} className="text-cobalt" /><h2 className="font-display uppercase tracking-tight text-xl">Accounts</h2></div>
        <ul className="space-y-2" data-testid="users-list">
          {users.map((u) => (
            <li key={u.username} className="flex items-center justify-between bg-secondary rounded-lg px-4 py-3" data-testid={`user-row-${u.username}`}>
              <span className="flex items-center gap-2 text-sm">
                {u.username}
                <Badge variant="secondary">{roleLabel(u.role)}</Badge>
                {u.username === meUsername && <span className="text-xs text-muted-foreground">(you)</span>}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setResetFor(u.username)} data-testid={`user-reset-${u.username}`} className="text-muted-foreground hover:text-cobalt transition-colors" aria-label={`Reset ${u.username}`}><KeyRound size={15} /></button>
                {u.username !== meUsername && (
                  <button onClick={() => remove(u.username)} data-testid={`user-delete-${u.username}`} className="text-muted-foreground hover:text-destructive transition-colors" aria-label={`Remove ${u.username}`}><Trash2 size={15} /></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4"><UserPlus size={18} className="text-cobalt" /><h2 className="font-display uppercase tracking-tight text-xl">Add Account</h2></div>
          <form onSubmit={create} className="space-y-3">
            <Input data-testid="nu-username" type="email" placeholder="Email" autoCapitalize="none" value={uName} onChange={(e) => setUName(e.target.value)} />
            <Input data-testid="nu-password" type="password" placeholder="Temporary password (min 8 characters)" value={uPass} onChange={(e) => setUPass(e.target.value)} />
            <select data-testid="nu-role" value={uRole} onChange={(e) => setURole(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="branch_manager">Branch Manager</option>
              <option value="owner">Website Manager</option>
            </select>
            <Button data-testid="nu-submit" type="submit" className="bg-cobalt hover:bg-cobalt/90"><Plus size={15} className="mr-1" /> Create account</Button>
          </form>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4"><KeyRound size={18} className="text-cobalt" /><h2 className="font-display uppercase tracking-tight text-xl">Reset a Password</h2></div>
          <p className="text-sm text-muted-foreground mb-4">If someone gets locked out, set a new temporary password for their account here, then share it with them. They'll be asked to choose their own on next login.</p>
          <form onSubmit={reset} className="space-y-3">
            <Input data-testid="rp-username" type="email" placeholder="Email to reset" autoCapitalize="none" value={resetFor} onChange={(e) => setResetFor(e.target.value)} />
            <Input data-testid="rp-password" type="password" placeholder="New temporary password (min 8 characters)" value={resetPass} onChange={(e) => setResetPass(e.target.value)} />
            <Button data-testid="rp-submit" type="submit" variant="outline">Reset password</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
